import { useEffect } from "react";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  faBoxArchive,
  faClockRotateLeft,
  faClone,
  faExternalLink,
  faPencil,
  faTrashCan,
} from "@fortawesome/pro-solid-svg-icons";
import ContextMenu from "@components/ContextMenu/ContextMenu";
import type { ArchiveAction } from "../archive/archiveCopy";
import type { ProjectRow } from "../types";

/** Duplicate opens its own modal; the archive actions share the confirm modal. */
export type RowAction = ArchiveAction | "duplicate";

export interface MenuTarget {
  row: ProjectRow;
  /** Change-request rows are sub-rows; only parents carry archive actions. */
  isChild: boolean;
  x: number;
  y: number;
}

export interface MenuOptions {
  href: string;
  isArchivedTab: boolean;
  canArchive: boolean;
  canPurge: boolean;
  canDuplicate: boolean;
}

export interface MenuEntry {
  key: string;
  label: string;
  icon: IconDefinition;
  href?: string;
  newTab?: boolean;
  action?: RowAction;
}

// Same entries as the legacy list's menu. Archive actions mirror the batch
// pill: privilege-gated and parent rows only, since change requests follow
// their parent server side. Duplicate is parent-only too: the copy would not
// keep the change-request link, leaving a stray standalone project.
export const projectMenuEntries = (
  target: Pick<MenuTarget, "isChild">,
  { href, isArchivedTab, canArchive, canPurge, canDuplicate }: MenuOptions
): MenuEntry[] => {
  const noun = target.isChild ? "Change Request" : "Project";
  const entries: MenuEntry[] = [
    { key: "edit", label: `Edit ${noun}`, icon: faPencil, href },
    {
      key: "edit-new-tab",
      label: `Edit ${noun} In New Tab`,
      icon: faExternalLink,
      href,
      newTab: true,
    },
  ];
  if (target.isChild) return entries;
  if (canDuplicate) {
    entries.push({
      key: "duplicate",
      label: "Duplicate",
      icon: faClone,
      action: "duplicate",
    });
  }
  if (!isArchivedTab && canArchive) {
    entries.push({
      key: "archive",
      label: "Archive",
      icon: faBoxArchive,
      action: "archive",
    });
  }
  if (isArchivedTab && canArchive) {
    entries.push({
      key: "restore",
      label: "Unarchive",
      icon: faClockRotateLeft,
      action: "restore",
    });
  }
  if (isArchivedTab && canPurge) {
    entries.push({
      key: "purge",
      label: "Purge",
      icon: faTrashCan,
      action: "purge",
    });
  }
  return entries;
};

interface Props {
  target: MenuTarget;
  entries: MenuEntry[];
  onAction: (action: RowAction, row: ProjectRow) => void;
  onClose: () => void;
}

const ProjectRowContextMenu = ({
  target,
  entries,
  onAction,
  onClose,
}: Props) => {
  useEffect(() => {
    const onPointer = (e: MouseEvent) => {
      const inside = (e.target as Element).closest?.(".context-menu-container");
      if (!inside) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onClose, true);
    window.addEventListener("resize", onClose);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onClose, true);
      window.removeEventListener("resize", onClose);
    };
  }, [onClose]);

  const menuItems = entries.map((entry, i) => ({
    href: entry.href ?? "#",
    newTab: !!entry.newTab,
    iconClass: entry.icon,
    style: { padding: "0 0.5em 0 0" },
    name: entry.label,
    // Separate the open actions from the row actions, like the legacy menu.
    hasBreakingLine: !entry.action && !!entries[i + 1]?.action,
    onClick: (e: React.MouseEvent) => {
      if (entry.action) {
        e.preventDefault();
        onAction(entry.action, target.row);
      }
      onClose();
    },
  }));

  // Portaled with page coordinates so a positioned ancestor can't offset it.
  return (
    <ContextMenu
      portal
      menuItems={menuItems}
      style={{ top: target.y, left: target.x }}
    />
  );
};

export default ProjectRowContextMenu;
